import { Card, CardContent } from "@/components/ui/card";
import { DollarSign } from "lucide-react";
import { formatCurrency, formatPercent } from "@/lib/config";
import { ValeurIndisponible, QB_DECONNECTE } from "@/components/dashboard/ValeurIndisponible";

export function SectionFinances({
  deptView, periodLabel, periodView, month, year,
  deptRevenus, deptDepenses, deptProfits, deptMarges, deptTaxes,
  ventesSignees, revenusEncaisses,
  productionPlanifieeTotal, productionRealiseeTotal, deptObjProd,
  MONTHS,
  canDo,
  qbDiag, // 📊 V2 : si QuickBooks déconnecté → « Indisponible » (jamais 0 $)
}) {
  const qbOff = Boolean(qbDiag && !qbDiag.connecte);
  const qbRaison = `${QB_DECONNECTE}${qbDiag?.raison ? ' — ' + qbDiag.raison : ''}`;
  const QB = ({ v, pct, testid }) => qbOff
    ? <ValeurIndisponible raison={qbRaison} action="Reconnecter" lienAction="/quickbooks" testid={testid} compact />
    : (pct ? formatPercent(v) : formatCurrency(v));
  return (
    <>
      {/* KPIs Financiers */}
      {canDo('dashboard_finances') && <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-purple-600" /> Finances (QuickBooks) {deptView !== 'global' ? deptView : ''} — {periodLabel}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Revenus</p>
              <p className="text-xl font-bold text-emerald-800 mt-1"><QB v={deptRevenus.total} testid="fin-revenus" /></p>
              {periodView === 'mensuel' && <p className="text-xs text-gray-500 mt-1">Bancaire: {formatCurrency(deptRevenus.bancaire)} | Cash: {formatCurrency(deptRevenus.cash)}</p>}
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/30">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-red-700 font-semibold uppercase tracking-wide">Dépenses</p>
              <p className="text-xl font-bold text-red-700 mt-1"><QB v={deptDepenses.total} testid="fin-depenses" /></p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Profit net</p>
              <p className={`text-xl font-bold mt-1 ${(deptProfits.apres_impots || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}><QB v={deptProfits.apres_impots} testid="fin-profit-net" /></p>
              <p className="text-xs text-gray-500 mt-1">Avant impôts: {formatCurrency(deptProfits.avant_impots)}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-slate-600 font-semibold uppercase tracking-wide">Marge brute</p>
              <p className={`text-xl font-bold mt-1 ${(deptMarges.brute || 0) >= 40 ? 'text-emerald-700' : (deptMarges.brute || 0) >= 25 ? 'text-amber-600' : 'text-red-600'}`}><QB v={deptMarges.brute} pct testid="fin-marge" /></p>
              <p className="text-xs text-gray-500 mt-1">Profit brut: {formatCurrency(deptProfits.brut)}</p>
            </CardContent>
          </Card>
        </div>
      </div>}

      {/* Ventes signées vs Revenus encaissés */}
      {canDo('dashboard_finances') && deptView !== 'LMDS' && deptView !== 'LOCATION' && (
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-sm font-semibold text-slate-700 mb-3">Ventes signees vs Revenus encaisses {deptView !== 'global' ? deptView : ''} — {periodLabel}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-xs text-emerald-600 font-semibold uppercase">Ventes signées</p>
                <p className="text-xl font-bold text-emerald-800 mt-1">{formatCurrency(ventesSignees)}</p>
                <p className="text-xs text-slate-400">Contrats signes {periodView === 'annuel' ? 'YTD' : periodLabel}</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-semibold uppercase">Revenus encaissés</p>
                <p className="text-xl font-bold text-blue-800 mt-1"><QB v={revenusEncaisses} testid="fin-encaisses" /></p>
                <p className="text-xs text-slate-400">Argent recu {periodView === 'annuel' ? 'YTD' : periodLabel}</p>
              </div>
            </div>
            {ventesSignees > 0 && !qbOff && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Taux d'encaissement</span>
                  <span className="font-bold">{(revenusEncaisses / ventesSignees * 100).toFixed(0)}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, revenusEncaisses / ventesSignees * 100)}%` }} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Production: Booking + Réalisé vs Objectif */}
      {canDo('dashboard_ventes') && (deptView === 'PRQC' || deptView === 'LMDS' || deptView === 'global') && (
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-sm font-semibold text-slate-700 mb-3">Production {deptView !== 'global' ? deptView + ' ' : ''}— {periodLabel}</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-semibold uppercase">Booking {periodView === 'annuel' ? 'YTD' : periodLabel}</p>
                <p className="text-xl font-bold text-blue-800 mt-1">{formatCurrency(productionPlanifieeTotal)}</p>
                <p className="text-xs text-slate-400">Contrats planifies au calendrier</p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-600 font-semibold uppercase">Realise {periodView === 'annuel' ? 'YTD' : periodLabel}</p>
                <p className="text-xl font-bold text-orange-800 mt-1">{formatCurrency(productionRealiseeTotal)}</p>
                <p className="text-xs text-slate-400">Travaux termines (prorata punches)</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600 font-semibold uppercase">Objectif</p>
                <p className="text-xl font-bold text-slate-800 mt-1">{formatCurrency(deptObjProd)}</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-600 font-semibold uppercase">Ecart (realise)</p>
                <p className={`text-xl font-bold mt-1 ${productionRealiseeTotal >= deptObjProd ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatCurrency(productionRealiseeTotal - deptObjProd)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
